import { ColumnDef } from '@tanstack/react-table'
import { DataTableColumnHeader } from '@/components/shared/data-table/data-table-column-header'
import { OrderStatus, Customer } from '@/types/order'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { formatDate } from '@/utils/orderUtils'
import { InvoicePdfLink } from '@/containers/Orders/components/InvoicePdfLink'

export interface InvoicedOrder {
  id: string
  createdAt: string
  order_status: OrderStatus
  order_code: string
  order_external_code: string | null
  invoice_code: string | null
  invoice_assigned_at: string | null
  invoice_pdf_url: string | null
  company_name: string | null
  purchased_by: Customer | null
}

export const columns: ColumnDef<InvoicedOrder>[] = [
  {
    accessorKey: 'invoice_code',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Invoice Code" />
    ),
    cell: ({ row }) => (
      <div className="text-xs font-mono">{row.getValue('invoice_code')}</div>
    ),
    meta: { width: '120px' },
  },
  {
    accessorKey: 'order_code',
    header: () => <div className="text-xs">Order</div>,
    enableSorting: false,
    cell: ({ row }) => {
      const orderCode = row.original.order_code
      const externalCode = row.original.order_external_code
      return (
        <div className="flex flex-col text-xs">
          <div>{orderCode}</div>
          {externalCode && (
            <div className="text-xs text-muted-foreground">
              {externalCode}
            </div>
          )}
        </div>
      )
    },
    meta: { width: '140px' },
  },
  {
    id: 'customer',
    accessorKey: 'purchased_by',
    header: () => <div className="text-xs">Customer</div>,
    enableSorting: false,
    cell: ({ row }) => {
      const purchasedBy = row.original.purchased_by
      const personName = purchasedBy
        ? `${purchasedBy.name ?? ''} ${purchasedBy.surname ?? ''}`.trim()
        : ''
      return (
        <div className="text-xs">
          {personName || row.original.company_name || '-'}
        </div>
      )
    },
  },
  {
    accessorKey: 'invoice_assigned_at',
    header: () => <div className="text-xs">Assigned</div>,
    // Not sortable: invoice_assigned_at is null for historical orders (see
    // Order.invoice_assigned_at doc), and it's unverified whether it's even
    // exposed on OrderOrderByWithRelationInput. Sorting desc would also
    // surface the null rows first (Postgres NULLS FIRST), which is the exact
    // outcome the default sort was chosen to avoid.
    enableSorting: false,
    cell: ({ row }) => {
      const assignedAt = row.getValue('invoice_assigned_at') as string | null
      return (
        <div className="text-xs">{assignedAt ? formatDate(assignedAt) : '-'}</div>
      )
    },
    meta: { width: '110px' },
  },
  {
    id: 'pdf',
    accessorKey: 'invoice_pdf_url',
    header: () => <div className="text-xs">PDF</div>,
    enableSorting: false,
    cell: ({ row }) => {
      const invoicePdfUrl = row.original.invoice_pdf_url
      if (!invoicePdfUrl) return <div className="text-xs">-</div>
      return (
        <InvoicePdfLink
          invoicePdfPath={invoicePdfUrl}
          invoiceCode={row.original.invoice_code}
        />
      )
    },
    meta: { width: '140px' },
  },
  {
    id: 'actions',
    cell: ({ row }) => {
      const navigate = useNavigate()
      return (
        <div className="flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/orders/${row.original.id}`)}
          >
            View order
          </Button>
        </div>
      )
    },
    meta: { width: '120px' },
  },
]
